import type { FamilyDefinition, FamilyParameter } from './families.js';

/**
 * Familles complémentaires, établies d'après la structure d'un DQE de bâtiment (lots usuels au Tchad
 * et en Afrique francophone, devis de référence TITE et Abdelatif) : terrassements complémentaires,
 * enduits, étanchéité, revêtements, peinture, charpente, menuiseries, plomberie, électricité,
 * assainissement et VRD. Ce sont des données : chaque entreprise peut les copier et les adapter.
 * Les compositions par défaut (carrelage, étanchéité, peinture…) sont des valeurs usuelles « à valider ».
 */
const m = (key: string, label: string, extra: Partial<FamilyParameter> = {}): FamilyParameter => ({ key, label, unit: 'm', ...extra });
const S = (label = 'Surface'): FamilyParameter => ({ key: 'S', label, unit: 'm2' });
const count = (label = 'Nombre par élément'): FamilyParameter => ({ key: 'u', label, unit: '', default: '1', integer: true });

export const EXTRA_FAMILIES: readonly FamilyDefinition[] = [
  // ── Terrassements
  {
    code: 'DECAPAGE',
    name: 'Décapage de la terre végétale',
    group: 'EARTHWORK',
    chapter: 'TERR',
    parameters: [S('Surface décapée'), m('e', 'Épaisseur décapée', { default: '0.20' })],
    outputs: [
      { key: 'surface', label: 'Surface décapée', unit: 'm2', formula: 'S * n', primary: true, kind: 'AREA' },
      { key: 'volume', label: 'Volume de terre enlevée', unit: 'm3', formula: 'S * e * n', kind: 'OTHER' },
    ],
  },
  {
    code: 'FOUILLE_MASSE',
    name: 'Fouille en pleine masse',
    group: 'EARTHWORK',
    chapter: 'TERR',
    parameters: [m('Lx', 'Longueur'), m('Ly', 'Largeur'), m('p', 'Profondeur', { section: true })],
    outputs: [{ key: 'volume', label: 'Fouille en pleine masse', unit: 'm3', formula: 'Lx * Ly * p * n', primary: true, kind: 'EXCAVATION' }],
  },
  {
    code: 'HERISSONNAGE',
    name: 'Hérissonnage en pierres sous dallage',
    group: 'EARTHWORK',
    chapter: 'FOND',
    defaults: { kits: ['HERISSONNAGE'] },
    parameters: [S(), m('e', 'Épaisseur', { default: '0.15', section: true })],
    outputs: [
      { key: 'volume', label: 'Volume de hérissonnage', unit: 'm3', formula: 'S * e * n', primary: true, kind: 'VOLUME' },
      { key: 'surface', label: 'Surface', unit: 'm2', formula: 'S * n', kind: 'OTHER' },
    ],
  },
  {
    code: 'TRAITEMENT_TERMITE',
    name: 'Traitement anti-termites du sol',
    group: 'EARTHWORK',
    chapter: 'TERR',
    parameters: [S('Surface traitée')],
    outputs: [{ key: 'surface', label: 'Surface traitée', unit: 'm2', formula: 'S * n', primary: true, kind: 'AREA' }],
  },
  // ── Béton complémentaire
  {
    code: 'FORME_PENTE',
    name: 'Forme de pente en béton (terrasse)',
    group: 'ROOF',
    chapter: 'ETANCH',
    defaults: { concrete: 'B250' },
    parameters: [S('Surface de la terrasse'), m('e_moy', 'Épaisseur moyenne', { default: '0.06', section: true })],
    outputs: [
      { key: 'volume', label: 'Béton de forme de pente', unit: 'm3', formula: 'S * e_moy * n', primary: true, kind: 'CONCRETE' },
      { key: 'surface', label: 'Surface', unit: 'm2', formula: 'S * n', kind: 'OTHER' },
    ],
  },
  {
    code: 'CANIVEAU',
    name: 'Caniveau en béton',
    group: 'OTHER',
    chapter: 'VRD',
    lineByType: true,
    defaults: { concrete: 'B250' },
    parameters: [m('L', 'Longueur'), m('b', 'Largeur intérieure', { section: true }), m('h', 'Profondeur intérieure', { section: true }), m('e', 'Épaisseur des parois et du fond', { default: '0.10', section: true })],
    outputs: [
      { key: 'volume', label: 'Béton du caniveau', unit: 'm3', formula: '((b + 2 * e) * (h + e) - b * h) * L * n', primary: true, kind: 'CONCRETE' },
      { key: 'longueur', label: 'Longueur', unit: 'm', formula: 'L * n', kind: 'OTHER' },
    ],
  },
  // ── Enduits
  {
    code: 'ENDUIT_MUR',
    name: 'Enduit sur murs',
    group: 'FINISH',
    chapter: 'ENDUIT',
    lineByType: true,
    openings: true,
    defaults: { mortar: 'ENDUIT' },
    description: 'Un type par enduit : intérieur lisse, extérieur, tyrolien (composition TYROLIEN_FINITION), hydrofuge (mortier NONE + composition ENDUIT_HYDROFUGE).',
    parameters: [m('L', 'Longueur développée'), m('H', 'Hauteur', { fromLevel: 'height' }), { key: 'faces', label: 'Nombre de faces', unit: '', default: '1', integer: true }],
    outputs: [{ key: 'surface', label: 'Surface enduite', unit: 'm2', formula: '(L * H * n - S_ouv) * faces', primary: true, kind: 'AREA' }],
  },
  {
    code: 'ENDUIT_PLAFOND',
    name: 'Enduit sous plafond (sous-face de dalle)',
    group: 'FINISH',
    chapter: 'ENDUIT',
    lineByType: true,
    openings: true,
    defaults: { mortar: 'ENDUIT' },
    parameters: [S('Surface de plafond')],
    outputs: [{ key: 'surface', label: 'Surface enduite', unit: 'm2', formula: 'S * n - S_ouv', primary: true, kind: 'AREA' }],
  },
  // ── Étanchéité
  {
    code: 'ETANCHEITE_TERRASSE',
    name: 'Étanchéité de toiture-terrasse',
    group: 'FINISH',
    chapter: 'ETANCH',
    lineByType: true,
    defaults: { kits: ['ETANCHE_BICOUCHE', 'ETANCHE_RELEVES'] },
    parameters: [S('Surface de la terrasse'), m('P', 'Périmètre des relevés', { default: '0' }), m('hr', 'Hauteur des relevés', { default: '0.20' })],
    outputs: [
      { key: 'surface', label: 'Surface étanchée', unit: 'm2', formula: 'S * n', primary: true, kind: 'AREA' },
      { key: 'releves', label: "Relevés d'étanchéité", unit: 'm', formula: 'P * n', kind: 'LENGTH' },
      { key: 'surface_releves', label: 'Surface des relevés', unit: 'm2', formula: 'P * hr * n', kind: 'OTHER' },
    ],
  },
  {
    code: 'PROTECTION_ETANCHEITE',
    name: "Protection d'étanchéité (gravillons)",
    group: 'FINISH',
    chapter: 'ETANCH',
    lineByType: true,
    defaults: { kits: ['PROTECTION_GRAVILLON'] },
    parameters: [S()],
    outputs: [{ key: 'surface', label: 'Surface protégée', unit: 'm2', formula: 'S * n', primary: true, kind: 'AREA' }],
  },
  {
    code: 'BADIGEON_SOUBASSEMENT',
    name: 'Badigeon bitumineux (soubassement, ouvrages enterrés)',
    group: 'FINISH',
    chapter: 'ETANCH',
    lineByType: true,
    defaults: { kits: ['BADIGEON_BITUME'] },
    parameters: [m('L', 'Longueur'), m('h', 'Hauteur traitée'), { key: 'faces', label: 'Nombre de faces', unit: '', default: '1', integer: true }],
    outputs: [{ key: 'surface', label: 'Surface badigeonnée', unit: 'm2', formula: 'L * h * faces * n', primary: true, kind: 'AREA' }],
  },
  {
    code: 'GARGOUILLE',
    name: 'Gargouille / trop-plein',
    group: 'FINISH',
    chapter: 'ETANCH',
    lineByType: true,
    parameters: [count()],
    outputs: [{ key: 'nombre', label: 'Nombre', unit: 'U', formula: 'u * n', primary: true, kind: 'COUNT' }],
  },
  // ── Revêtements
  {
    code: 'CARRELAGE_SOL',
    name: 'Carrelage de sol',
    group: 'FINISH',
    chapter: 'REVET',
    lineByType: true,
    defaults: { kits: ['CARRELAGE_SOL', 'PLINTHES'] },
    parameters: [S(), m('P', 'Périmètre (plinthes)', { default: '0' })],
    outputs: [
      { key: 'surface', label: 'Surface carrelée', unit: 'm2', formula: 'S * n', primary: true, kind: 'AREA' },
      { key: 'plinthe', label: 'Plinthes', unit: 'm', formula: 'P * n', kind: 'LENGTH' },
    ],
  },
  {
    code: 'FAIENCE',
    name: 'Faïence murale',
    group: 'FINISH',
    chapter: 'REVET',
    lineByType: true,
    openings: true,
    defaults: { kits: ['FAIENCE'] },
    parameters: [m('L', 'Longueur développée'), m('h', 'Hauteur de pose')],
    outputs: [{ key: 'surface', label: 'Surface de faïence', unit: 'm2', formula: 'L * h * n - S_ouv', primary: true, kind: 'AREA' }],
  },
  {
    code: 'CHAPE',
    name: 'Chape au mortier de ciment',
    group: 'FINISH',
    chapter: 'REVET',
    lineByType: true,
    defaults: { mortar: 'CHAPE' },
    parameters: [S()],
    outputs: [{ key: 'surface', label: 'Surface de chape', unit: 'm2', formula: 'S * n', primary: true, kind: 'AREA' }],
  },
  // ── Peinture
  {
    code: 'PEINTURE_MUR',
    name: 'Peinture sur murs',
    group: 'FINISH',
    chapter: 'PEINT',
    lineByType: true,
    openings: true,
    defaults: { kits: ['PEINTURE_VINYLE_2C'] },
    parameters: [m('L', 'Longueur développée'), m('H', 'Hauteur', { fromLevel: 'height' }), { key: 'faces', label: 'Nombre de faces', unit: '', default: '1', integer: true }],
    outputs: [{ key: 'surface', label: 'Surface peinte', unit: 'm2', formula: '(L * H * n - S_ouv) * faces', primary: true, kind: 'AREA' }],
  },
  {
    code: 'PEINTURE_SURFACE',
    name: 'Peinture sur surface (plafonds, menuiseries…)',
    group: 'FINISH',
    chapter: 'PEINT',
    lineByType: true,
    defaults: { kits: ['PEINTURE_VINYLE_2C'] },
    description: 'Plafonds : peinture vinylique ; menuiseries bois : PEINTURE_GLYCERO_2C ; menuiseries métalliques : PEINTURE_METAL.',
    parameters: [S('Surface à peindre')],
    outputs: [{ key: 'surface', label: 'Surface peinte', unit: 'm2', formula: 'S * n', primary: true, kind: 'AREA' }],
  },
  // ── Charpente, couverture, faux plafond
  {
    code: 'CHARPENTE',
    name: 'Charpente (bois ou métallique)',
    group: 'ROOF',
    chapter: 'CHARP',
    lineByType: true,
    description: 'Payée au m² couvert ; les matériaux (bois, profilés) se décrivent par une composition propre à l’entreprise.',
    parameters: [S('Surface couverte en plan')],
    outputs: [{ key: 'surface', label: 'Surface de charpente', unit: 'm2', formula: 'S * n', primary: true, kind: 'AREA' }],
  },
  {
    code: 'GOUTTIERE',
    name: 'Gouttière',
    group: 'FINISH',
    chapter: 'COUV',
    lineByType: true,
    defaults: { kits: ['GOUTTIERE'] },
    parameters: [m('L', 'Longueur')],
    outputs: [{ key: 'longueur', label: 'Longueur', unit: 'm', formula: 'L * n', primary: true, kind: 'LENGTH' }],
  },
  {
    code: 'DESCENTE_EP',
    name: "Descente d'eaux pluviales",
    group: 'FINISH',
    chapter: 'COUV',
    lineByType: true,
    defaults: { kits: ['DESCENTE_EP'] },
    parameters: [m('L', 'Hauteur de descente')],
    outputs: [{ key: 'longueur', label: 'Longueur', unit: 'm', formula: 'L * n', primary: true, kind: 'LENGTH' }],
  },
  {
    code: 'FAUX_PLAFOND',
    name: 'Faux plafond',
    group: 'FINISH',
    chapter: 'FAUX_PLAFOND',
    lineByType: true,
    defaults: { kits: ['FAUX_PLAFOND'] },
    parameters: [S()],
    outputs: [{ key: 'surface', label: 'Surface de faux plafond', unit: 'm2', formula: 'S * n', primary: true, kind: 'AREA' }],
  },
  // ── Menuiseries (une ligne par type : porte isoplane 90 × 210, fenêtre 120 × 120…)
  ...(
    [
      ['MENUISERIE_BOIS', 'Menuiserie bois', 'MENUIS_BOIS'],
      ['MENUISERIE_METAL', 'Menuiserie métallique', 'MENUIS_MET'],
      ['MENUISERIE_ALU', 'Menuiserie aluminium', 'MENUIS_ALU'],
    ] as const
  ).map(
    ([code, name, chapter]): FamilyDefinition => ({
      code,
      name,
      group: 'OTHER',
      chapter,
      lineByType: true,
      description: 'Fourniture et pose à l’unité. La surface (largeur × hauteur) sert aux peintures et aux vérifications.',
      parameters: [m('l', 'Largeur', { section: true }), m('h', 'Hauteur', { section: true })],
      outputs: [
        { key: 'nombre', label: 'Nombre', unit: 'U', formula: 'n', primary: true, kind: 'COUNT' },
        { key: 'surface', label: 'Surface de baie', unit: 'm2', formula: 'l * h * n', kind: 'OTHER' },
      ],
    }),
  ),
  // ── Plomberie sanitaire, électricité
  {
    code: 'APPAREIL_SANITAIRE',
    name: 'Appareil sanitaire (WC, lavabo, douche, évier…)',
    group: 'OTHER',
    chapter: 'PLOMB',
    lineByType: true,
    parameters: [count()],
    outputs: [{ key: 'nombre', label: 'Nombre', unit: 'U', formula: 'u * n', primary: true, kind: 'COUNT' }],
  },
  {
    code: 'CANALISATION',
    name: 'Canalisation (alimentation, évacuation)',
    group: 'FINISH',
    chapter: 'PLOMB',
    lineByType: true,
    parameters: [m('L', 'Longueur')],
    outputs: [{ key: 'longueur', label: 'Longueur', unit: 'm', formula: 'L * n', primary: true, kind: 'LENGTH' }],
  },
  {
    code: 'POINT_ELECTRIQUE',
    name: 'Point électrique (foyer lumineux, prise, interrupteur…)',
    group: 'OTHER',
    chapter: 'ELEC',
    lineByType: true,
    parameters: [count()],
    outputs: [{ key: 'nombre', label: 'Nombre', unit: 'U', formula: 'u * n', primary: true, kind: 'COUNT' }],
  },
  {
    code: 'CABLE_ELECTRIQUE',
    name: 'Câble / conduit électrique',
    group: 'FINISH',
    chapter: 'ELEC',
    lineByType: true,
    parameters: [m('L', 'Longueur')],
    outputs: [{ key: 'longueur', label: 'Longueur', unit: 'm', formula: 'L * n', primary: true, kind: 'LENGTH' }],
  },
  // ── Assainissement autonome
  {
    code: 'FOSSE_SEPTIQUE',
    name: 'Fosse septique (radier, parois maçonnées, dalle)',
    group: 'OTHER',
    chapter: 'ASSAIN',
    lineByType: true,
    buried: true,
    defaults: { kits: ['ENDUIT_HYDROFUGE'] },
    parameters: [
      m('Li', 'Longueur intérieure', { section: true }),
      m('li', 'Largeur intérieure', { section: true }),
      m('Hi', 'Hauteur intérieure', { section: true }),
      m('e', 'Épaisseur des parois', { default: '0.15' }),
      m('er', 'Épaisseur du radier', { default: '0.15' }),
      m('ed', 'Épaisseur de la dalle', { default: '0.12' }),
      m('s', 'Surlargeur de fouille (par côté)', { default: '0.30' }),
    ],
    outputs: [
      { key: 'enduit', label: 'Enduit hydrofuge intérieur', unit: 'm2', formula: '(2 * (Li + li) * Hi + Li * li) * n', primary: true, kind: 'AREA' },
      { key: 'fouille', label: 'Fouille', unit: 'm3', formula: '(Li + 2 * e + 2 * s) * (li + 2 * e + 2 * s) * (Hi + er) * n', kind: 'EXCAVATION' },
      { key: 'radier', label: 'Béton du radier', unit: 'm3', formula: '(Li + 2 * e) * (li + 2 * e) * er * n', kind: 'CONCRETE' },
      { key: 'dalle', label: 'Béton de la dalle de couverture', unit: 'm3', formula: '(Li + 2 * e) * (li + 2 * e) * ed * n', kind: 'CONCRETE' },
      { key: 'parois', label: 'Maçonnerie des parois', unit: 'm2', formula: '2 * (Li + li + 2 * e) * Hi * n', kind: 'MASONRY' },
      { key: 'volume_parois', label: 'Volume des parois', unit: 'm3', formula: '2 * (Li + li + 2 * e) * Hi * e * n', kind: 'MASONRY_VOLUME' },
      { key: 'vide', label: 'Volume intérieur (non remblayé)', unit: 'm3', formula: 'Li * li * Hi * n', kind: 'MASONRY_VOLUME' },
    ],
  },
  {
    code: 'PUISARD',
    name: 'Puisard (puits perdu) circulaire',
    group: 'OTHER',
    chapter: 'ASSAIN',
    lineByType: true,
    buried: true,
    parameters: [m('D', 'Diamètre intérieur', { section: true }), m('H', 'Profondeur', { section: true }), m('e', 'Épaisseur des parois', { default: '0.15' }), m('ed', 'Épaisseur de la dalle', { default: '0.10' }), m('s', 'Surlargeur de fouille', { default: '0.30' })],
    outputs: [
      { key: 'parois', label: 'Maçonnerie des parois', unit: 'm2', formula: 'pi() * (D + e) * H * n', primary: true, kind: 'MASONRY' },
      { key: 'fouille', label: 'Fouille', unit: 'm3', formula: 'pi() * (D / 2 + e + s) ^ 2 * H * n', kind: 'EXCAVATION' },
      { key: 'dalle', label: 'Béton de la dalle de couverture', unit: 'm3', formula: 'pi() * (D / 2 + e) ^ 2 * ed * n', kind: 'CONCRETE' },
      { key: 'remplissage', label: 'Remplissage en pierres', unit: 'm3', formula: 'pi() * (D / 2) ^ 2 * H * n', kind: 'VOLUME' },
      { key: 'volume_parois', label: 'Volume des parois et du remplissage', unit: 'm3', formula: 'pi() * (D / 2 + e) ^ 2 * H * n', kind: 'MASONRY_VOLUME' },
    ],
  },
  {
    code: 'REGARD',
    name: 'Regard (visite, branchement)',
    group: 'OTHER',
    chapter: 'ASSAIN',
    lineByType: true,
    parameters: [m('l', 'Côté intérieur', { section: true }), m('h', 'Profondeur', { section: true })],
    outputs: [{ key: 'nombre', label: 'Nombre', unit: 'U', formula: 'n', primary: true, kind: 'COUNT' }],
  },
];
